import tokens from '@kluein/design-tokens';
import { Box, Button, Text, Heading } from '@kluein/klue-ui';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import RivalGrid, { type RivalGridProps } from '../rival-grid/RivalGrid';

const DASHBOARD_CONTAINER_WIDTH = '100%';

type RivalGroupProps = {
  id?: number;
  name: string;
  rivals?: number[];
  isCollapsible?: boolean;
  rowOrder?: number;
} & Omit<RivalGridProps, 'rivalGroupId'>;

type CollapsibleGroupProps = {
  name: string;
  onCollapse: VoidFunction;
  collapsed: boolean;
};

const MotionBox = motion(Box);

const CollapsibleGroup = ({
  onCollapse,
  collapsed,
  grid,
  name,
}: CollapsibleGroupProps & { grid: React.ReactNode }) => (
  <Box
    data-test-id={TEST_IDS.dashboard.rivalGroup.container}
    data-test-target={name}
    flex={{ shrink: 0 }}
    width={DASHBOARD_CONTAINER_WIDTH}
    border={{
      side: 'bottom',
      size: 'small',
      color: tokens.color.neutral.lightgrey.main,
    }}
  >
    <Button
      size="large"
      variant="plain"
      onClick={onCollapse}
      data-test-id={TEST_IDS.dashboard.rivalGroup.title}
      rightSlot={
        collapsed ? (
          <CaretUp
            color={tokens.color.neutral.black.main}
            size={tokens.iconSize.xsmall}
          />
        ) : (
          <CaretDown
            color={tokens.color.neutral.black.main}
            size={tokens.iconSize.xsmall}
          />
        )
      }
      data-tracking-id={TRACKING_IDS.dashboard.rivalGroup.accordion}
    >
      <Box pad={{ vertical: 'large' }}>
        <Text
          size="xlarge"
          fontWeight="semibold"
          textAlign="start"
          data-test-id={TEST_IDS.dashboard.rivalGroup.name}
        >
          {name}
        </Text>
      </Box>
    </Button>
    <AnimatePresence>
      {!collapsed && (
        <MotionBox
          initial="open"
          animate="open"
          exit="collapsed"
          variants={{
            open: { height: 'auto' },
            collapsed: { height: '0px' },
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          overflow={{ vertical: 'auto' }}
        >
          {grid}
        </MotionBox>
      )}
    </AnimatePresence>
  </Box>
);

const RivalGroup = ({
  id,
  sort,
  name,
  rivals,
  isCollapsible,
  rowOrder,
}: RivalGroupProps) => {
  const [collapsed, setCollapsed] = useState(false);

  if (rivals?.length === 0) {
    return null;
  }

  const onCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  const grid = (
    <RivalGrid
      key={`${id}-${sort}`}
      rivalGroupId={id}
      sort={sort}
      rowOrder={rowOrder}
    />
  );

  return isCollapsible ? (
    <CollapsibleGroup
      name={name}
      collapsed={collapsed}
      onCollapse={onCollapse}
      grid={grid}
    />
  ) : (
    <Box
      data-test-id={TEST_IDS.dashboard.rivalGroup.container}
      flex={{ shrink: 0 }}
      width={DASHBOARD_CONTAINER_WIDTH}
    >
      {!!name && <Heading level="2">{name}</Heading>}
      {grid}
    </Box>
  );
};

export default RivalGroup;
